/**
 * Generic entity context menu hook.
 *
 * Provides a right-click context menu for any entity list item. Modules can
 * inject their own items via `moduleItems`.
 *
 * "Link to X" submenus (Add/Remove to a group, a project, …) are NOT
 * hardcoded here. Any module can declare a DECLARATIVE `entityLink`
 * contribution on its `ModuleDefinition` (Windows "Send to…" style); this hook
 * discovers all contributors across `APP_MODULES` and renders one submenu per
 * contributor. The host therefore never imports a specific module's queries.
 *
 * Usage:
 *   const ctx = useEntityContextMenu<MyData>({
 *     entityId: (data) => data.id,
 *     moduleItems: (data) => [{ id: "reply", label: "Reply", icon: "send" }],
 *   });
 *
 *   <ModuleListItem onContextMenu={(e) => ctx.open(e, item)}>
 *   {ctx.menu}   // renders ContextMenu when open
 */

import { useCallback, useMemo, type ReactNode, createElement } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useContextMenu } from "../components/ui/useContextMenu";
import { ContextMenu } from "../components/ui/ContextMenu";
import type { ContextMenuEntry } from "../components/ui/ContextMenu";
import type { IconName } from "../components/ui/Icon";
import { useAppRuntime } from "../providers/AppRuntimeProvider";
import type { EntityLinkContribution } from "../runtime/contracts/module";

export interface EntityContextMenuConfig<T> {
  /** Extract entity ID from the data attached to the right-clicked item. */
  readonly entityId: (data: T) => string;
  /** Optional module-specific menu items. Placed before the link submenus. */
  readonly moduleItems?: (data: T) => readonly ContextMenuEntry[];
  /** Optional handler for module-specific actions (non-link). */
  readonly onModuleAction?: (data: T, actionId: string) => void;
  /** Suppress specific "Link to X" submenus by their `idPrefix`
   *  (e.g. ["project"] to hide "Link to Project" in a given module). */
  readonly disabledEntityLinks?: readonly string[];
  /** Enable pin/unpin item. If provided, item's is_pinned state is read from this callback. */
  readonly isPinned?: (data: T) => boolean;
  /** Called on delete action. If omitted, delete item is hidden. */
  readonly onDelete?: (data: T) => void;
  /** Called on archive action. If omitted, archive item is hidden. */
  readonly onArchive?: (data: T) => void;
  /** Called on rename action. If omitted, rename item is hidden. */
  readonly onRename?: (data: T) => void;
}

export interface EntityContextMenuResult<T> {
  /** Pass to ModuleListItem's onContextMenu. */
  readonly open: (event: React.MouseEvent, data: T) => void;
  /** Close the menu programmatically. */
  readonly close: () => void;
  /** The currently right-clicked data, or null. */
  readonly data: T | null;
  /** Whether the menu is visible. */
  readonly isOpen: boolean;
  /** Rendered ContextMenu element (or null when closed). Place in JSX. */
  readonly menu: ReactNode;
}

interface LinkTarget {
  readonly id: string;
  readonly name: string;
  readonly color?: string;
}
type LinkTargetList = readonly LinkTarget[] | { readonly items: readonly LinkTarget[] };

function normalizeTargets(value: LinkTargetList | undefined): readonly LinkTarget[] {
  if (!value) return [];
  return Array.isArray(value)
    ? value
    : (value as { items?: readonly LinkTarget[] }).items ?? [];
}

export function useEntityContextMenu<T>(
  config: EntityContextMenuConfig<T>,
): EntityContextMenuResult<T> {
  const runtime = useAppRuntime();
  const queryClient = useQueryClient();
  const contextMenu = useContextMenu<T>();

  const currentEntityId = contextMenu.state.data
    ? config.entityId(contextMenu.state.data)
    : undefined;

  // Declarative "Link to X" contributors discovered across all registered
  // modules (the same registry the provider populates from APP_MODULES).
  const linkContributors = useMemo<readonly EntityLinkContribution[]>(
    () =>
      runtime.modules
        .list()
        .map((m) => m.entityLink)
        .filter((c): c is EntityLinkContribution => c != null)
        .filter((c) => !config.disabledEntityLinks?.includes(c.idPrefix)),
    [runtime, config.disabledEntityLinks],
  );

  // Two queries per contributor: all targets + targets linked to this entity.
  // Keys live under the contributor's `invalidateKey` prefix so an add/remove
  // invalidation refreshes both these and the owning module's own queries.
  const results = useQueries({
    queries: linkContributors.flatMap((c) => [
      {
        queryKey: [c.invalidateKey, "__ctxmenu", "list"] as const,
        queryFn: (): Promise<LinkTargetList> =>
          runtime.transport.rpc<LinkTargetList>(c.listMethod, { limit: 1000 }),
        staleTime: 30_000,
      },
      {
        queryKey: [c.invalidateKey, "__ctxmenu", "forEntity", currentEntityId ?? ""] as const,
        queryFn: (): Promise<readonly LinkTarget[]> =>
          runtime.transport.rpc<readonly LinkTarget[]>(c.forEntityMethod, {
            entity_id: currentEntityId,
          }),
        enabled: !!currentEntityId,
        staleTime: 10_000,
      },
    ]),
  });

  const linkData = useMemo(
    () =>
      linkContributors.map((contributor, i) => ({
        contributor,
        all: normalizeTargets(results[i * 2]?.data),
        linkedIds: new Set(
          normalizeTargets(results[i * 2 + 1]?.data).map((t) => t.id),
        ),
      })),
    [linkContributors, results],
  );

  const items = useMemo((): readonly ContextMenuEntry[] => {
    const entries: ContextMenuEntry[] = [];

    // Rename item first — @tested-by: tst_fe_scn_list_rename_001
    if (config.onRename && contextMenu.state.data) {
      entries.push({
        id: "_rename",
        label: "Rename",
        icon: "pencil" as IconName,
      });
    }

    // Module-specific items
    if (contextMenu.state.data && config.moduleItems) {
      entries.push(...config.moduleItems(contextMenu.state.data));
    }

    // "Link to X" submenus — one per declarative contributor. A single
    // separator precedes the whole block (not between submenus).
    if (currentEntityId) {
      let linkSeparatorAdded = false;
      for (const { contributor, all, linkedIds } of linkData) {
        if (all.length === 0) continue;

        if (entries.length > 0 && !linkSeparatorAdded) {
          entries.push({ type: "separator" });
          linkSeparatorAdded = true;
        }

        const children: ContextMenuEntry[] = [];
        for (const target of all) {
          const linked = linkedIds.has(target.id);
          children.push({
            id: `${contributor.idPrefix}-${linked ? "remove" : "add"}:${target.id}`,
            label: linked ? `${target.name} ✓` : target.name,
            icon: (linked ? "check" : "plus") as IconName,
            ...(contributor.hasColor && target.color
              ? { colorDot: target.color }
              : {}),
          });
        }

        entries.push({
          type: "submenu",
          label: contributor.label,
          icon: contributor.icon,
          children,
        });
      }
    }

    // ── Pin / Archive / Delete ──
    if (currentEntityId && contextMenu.state.data) {
      const hasPrev = entries.length > 0;
      if (config.isPinned || config.onArchive || config.onDelete) {
        if (hasPrev) entries.push({ type: "separator" });
      }

      if (config.isPinned) {
        const pinned = config.isPinned(contextMenu.state.data);
        entries.push({
          id: pinned ? "_unpin" : "_pin",
          label: pinned ? "Unpin" : "Pin",
          icon: "pin" as IconName,
        });
      }

      if (config.onArchive) {
        entries.push({
          id: "_archive",
          label: "Archive",
          icon: "archive" as IconName,
        });
      }

      if (config.onDelete) {
        entries.push({
          id: "_delete",
          label: "Delete",
          icon: "trash-2" as IconName,
          variant: "danger",
        });
      }
    }

    return entries;
  }, [linkData, currentEntityId, contextMenu.state.data, config]);

  const handleSelect = useCallback(
    (actionId: string) => {
      if (!currentEntityId) {
        contextMenu.close();
        return;
      }

      const sepIdx = actionId.indexOf(":");
      const verb = sepIdx >= 0 ? actionId.slice(0, sepIdx) : actionId;
      const linkContributor =
        sepIdx >= 0
          ? linkContributors.find(
              (c) => verb === `${c.idPrefix}-add` || verb === `${c.idPrefix}-remove`,
            )
          : undefined;

      if (linkContributor) {
        const targetId = actionId.slice(sepIdx + 1);
        const isAdd = verb === `${linkContributor.idPrefix}-add`;
        const method = isAdd ? linkContributor.addMethod : linkContributor.removeMethod;
        void runtime.transport
          .rpc(method, {
            [linkContributor.idParam]: targetId,
            entity_id: currentEntityId,
          })
          .then(() => {
            void queryClient.invalidateQueries({
              queryKey: [linkContributor.invalidateKey],
            });
          });
      } else if (actionId === "_pin" || actionId === "_unpin") {
        const method = actionId === "_pin" ? "graph.entity.pin" : "graph.entity.unpin";
        void runtime.transport
          .rpc(method, { entity_id: currentEntityId })
          .then(() => {
            void queryClient.invalidateQueries({ refetchType: "all" });
          });
      } else if (actionId === "_archive") {
        void runtime.transport
          .rpc("graph.entity.archive", { entity_id: currentEntityId })
          .then(() => {
            void queryClient.invalidateQueries({ refetchType: "all" });
          });
      // @tested-by: tst_fe_scn_list_rename_001
      } else if (actionId === "_rename" && contextMenu.state.data && config.onRename) {
        config.onRename(contextMenu.state.data);
      } else if (actionId === "_delete" && contextMenu.state.data && config.onDelete) {
        config.onDelete(contextMenu.state.data);
      } else if (contextMenu.state.data && config.onModuleAction) {
        config.onModuleAction(contextMenu.state.data, actionId);
      }

      contextMenu.close();
    },
    [currentEntityId, runtime, queryClient, contextMenu, config, linkContributors],
  );

  const menu =
    contextMenu.state.isOpen && contextMenu.state.data && items.length > 0
      ? createElement(ContextMenu, {
          items,
          position: contextMenu.state.position,
          onSelect: handleSelect,
          onClose: contextMenu.close,
        })
      : null;

  return {
    open: contextMenu.open,
    close: contextMenu.close,
    data: contextMenu.state.data,
    isOpen: contextMenu.state.isOpen,
    menu,
  };
}
